/**
 * Utility functions to access ad source information detected by the edge function
 * GDPR Compliant - respects cookie consent
 *
 * Uses a single ad_params cookie containing all tracking data in JSON format
 */

/**
 * Ad tracking data structure
 */
interface AdParams {
  source: string
  clickId: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
  utm_source: string | null
  utm_medium: string | null
  utm_id: string | null
  landingPage: string
  ad_first_touch: string
  splitTestVariant?: string // Custom label for split test variant (e.g., "control", "try-subdomain", etc.)
  splitTestStatus?: "active" | "logging" | "inactive" // Status of split test: active (redirecting), logging (tracking only), inactive (disabled)
}

/**
 * Get a cookie value by name
 */
const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") return null

  const matches = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  )
  return matches ? decodeURIComponent(matches[1]) : null
}

/**
 * Delete a cookie by name
 */
const deleteCookie = (name: string): void => {
  if (typeof document === "undefined") return

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.sens.ai`
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
}

/**
 * Get parsed ad params from cookie
 */
const getAdParams = (): AdParams | null => {
  const paramsJson = getCookieValue("ad_params")
  if (!paramsJson) return null

  try {
    return JSON.parse(paramsJson) as AdParams
  } catch (err) {
    console.error("Failed to parse ad_params cookie:", err)
    return null
  }
}

/**
 * Delete all ad tracking cookies (GDPR compliance)
 * Call this when user withdraws consent
 */
export const deleteAdTrackingCookies = (): void => {
  deleteCookie("ad_params")
  console.log("Ad tracking cookies deleted")
}

/**
 * Check if the current visitor came from a Google ad
 */
export const isFromGoogleAd = (): boolean => {
  const params = getAdParams()
  return params?.source === "google_ads"
}

/**
 * Check if the current visitor came from a Facebook/Instagram ad
 */
export const isFromFacebookAd = (): boolean => {
  const params = getAdParams()
  return params?.source === "facebook_ads"
}

/**
 * Check if the current visitor came from any ad source
 */
export const isFromAd = (): boolean => {
  return getAdParams() !== null
}

/**
 * Get the ad source (google_ads, facebook_ads, or null)
 */
export const getAdSource = (): string | null => {
  return getAdParams()?.source ?? null
}

/**
 * Get the click ID (gclid or fbclid)
 */
export const getAdClickId = (): string | null => {
  return getAdParams()?.clickId ?? null
}

/**
 * Get UTM campaign parameter
 */
export const getUtmCampaign = (): string | null => {
  return getAdParams()?.utm_campaign ?? null
}

/**
 * Get UTM content parameter
 */
export const getUtmContent = (): string | null => {
  return getAdParams()?.utm_content ?? null
}

/**
 * Get UTM term parameter
 */
export const getUtmTerm = (): string | null => {
  return getAdParams()?.utm_term ?? null
}

/**
 * Get UTM source parameter
 */
export const getUtmSource = (): string | null => {
  return getAdParams()?.utm_source ?? null
}

/**
 * Get the landing page where the user first arrived
 */
export const getAdLandingPage = (): string | null => {
  return getAdParams()?.landingPage ?? null
}

/**
 * Get the timestamp of when the user first arrived from an ad
 */
export const getAdFirstTouch = (): Date | null => {
  const params = getAdParams()
  return params?.ad_first_touch ? new Date(params.ad_first_touch) : null
}

/**
 * Get the split test variant label (e.g., "control", "try-subdomain", etc.)
 */
export const getSplitTestVariant = (): string | null => {
  return getAdParams()?.splitTestVariant ?? null
}

/**
 * Check if user is in a specific variant
 * @param variantLabel - The label to check for (e.g., "try-subdomain")
 */
export const isVariant = (variantLabel: string): boolean => {
  return getSplitTestVariant() === variantLabel
}

/**
 * Check if user is in the control group
 * @param controlLabel - The control label (default: "control")
 */
export const isControlVariant = (controlLabel: string = "control"): boolean => {
  return getSplitTestVariant() === controlLabel
}

/**
 * @deprecated Use isVariant() or isControlVariant() instead
 * Check if user is in the test variant (variant B)
 */
export const isTestVariant = (): boolean => {
  const variant = getSplitTestVariant()
  return variant !== null && variant !== "control" && variant !== "A"
}

/**
 * Check if split testing is currently enabled
 */
export const isSplitTestEnabled = (): boolean => {
  const status = getAdParams()?.splitTestStatus
  return status === "active" || status === "logging"
}

/**
 * Get split test status
 */
export const getSplitTestStatus = ():
  | "active"
  | "logging"
  | "inactive"
  | null => {
  return getAdParams()?.splitTestStatus ?? null
}

/**
 * Get all ad tracking information as an object
 */
export const getAdInfo = (): {
  source: string | null
  clickId: string | null
  campaign: string | null
  content: string | null
  term: string | null
  utmSource: string | null
  utmMedium: string | null
  utmId: string | null
  landingPage: string | null
  firstTouch: Date | null
  isFromAd: boolean
  isFromGoogle: boolean
  isFromFacebook: boolean
  splitTestVariant: string | null
  isTestVariant: boolean
  isSplitTestActive: boolean
  splitTestStatus: "active" | "logging" | "inactive" | null
} => {
  const params = getAdParams()

  return {
    source: params?.source ?? null,
    clickId: params?.clickId ?? null,
    campaign: params?.utm_campaign ?? null,
    content: params?.utm_content ?? null,
    term: params?.utm_term ?? null,
    utmSource: params?.utm_source ?? null,
    utmMedium: params?.utm_medium ?? null,
    utmId: params?.utm_id ?? null,
    landingPage: params?.landingPage ?? null,
    firstTouch: params?.ad_first_touch ? new Date(params.ad_first_touch) : null,
    isFromAd: params !== null,
    isFromGoogle: params?.source === "google_ads",
    isFromFacebook: params?.source === "facebook_ads",
    splitTestVariant: params?.splitTestVariant ?? null,
    isSplitTestActive:
      params?.splitTestStatus === "active" ||
      params?.splitTestStatus === "logging",
    splitTestStatus: params?.splitTestStatus ?? null,
    isTestVariant:
      params?.splitTestVariant !== null &&
      params?.splitTestVariant !== "control" &&
      params?.splitTestVariant !== "A" &&
      params?.splitTestVariant !== "none",
  }
}

/**
 * Get all raw ad parameters (useful for debugging)
 */
export const getAllAdParams = (): AdParams | null => {
  return getAdParams()
}
